/**
 * Wallet Service
 * Flutter source: lib/data/repository/wallet_repository.dart + lib/data/data_sources/wallet_data_source.dart
 *
 * Domain service for wallet operations: fetching transactions and wallet balance.
 */
import { auth } from '@/data/services/firebase';
import { firestoreService } from '@/data/services/firestoreService';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { WalletStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { WalletEntity } from '@/domain/entities/wallet/walletEntity';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { AppTransactionRemapper } from '@/domain/remappers/transaction/appTransactionRemapper';
import { WalletRemapper } from '@/domain/remappers/wallet/walletRemapper';
import type { FirestoreService } from '@/data/services/firestoreService';
import type { Unsubscribe } from 'firebase/firestore';
import type { PageCursor } from '@/data/types/json';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class WalletService {
  private static instance: WalletService;
  private firestoreServiceInstance: FirestoreService;

  constructor(firestoreServiceInstance: FirestoreService = firestoreService) {
    this.firestoreServiceInstance = firestoreServiceInstance;
  }

  /**
   * Singleton accessor for symmetry with `PremiumPackagesActionsService`.
   * Use cases should default to this instead of constructing a fresh service.
   * The public constructor is retained for test injection of a stub
   * `FirestoreService`.
   */
  static getInstance(): WalletService {
    if (!this.instance) {
      this.instance = new WalletService();
    }
    return this.instance;
  }

  /**
   * Fetch paginated wallet transactions (owner-scoped).
   *
   * Calls the regular owner-scoped `getWalletTransactions(userId, cursor,
   * limitCount)` data path. The admin "all users" variant is a separate method,
   * `fetchAdminTransactions`, so there is no `isFromAdmin` flag on this path.
   */
  async fetchTransactions(
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ transactions: AppTransactionEntity[]; hasMore: boolean; cursor?: PageCursor }> {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { transactions: [], hasMore: false };
    }

    try {
      const response = await this.firestoreServiceInstance.getWalletTransactions(
        userId,
        cursor,
        limitCount,
      );

      const transactions = response.items.map(item =>
        AppTransactionRemapper.toEntity(AppTransactionRemapper.fromJson(item)),
      );

      return {
        transactions,
        hasMore: response.hasMore,
        cursor: response.cursor,
      };
    } catch (error) {
      appLogger.error('Error fetching transactions', 'WalletService.fetchTransactions', { error: error as Error });
      return { transactions: [], hasMore: false };
    }
  }

  /**
   * Fetch ALL users' transactions (admin variant). Calls the dedicated
   * `getAdminWalletTransactions` data path — no `isFromAdmin` flag. The signed-in
   * guard is kept so an unauthenticated caller short-circuits to empty.
   */
  async fetchAdminTransactions(
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ transactions: AppTransactionEntity[]; hasMore: boolean; cursor?: PageCursor }> {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { transactions: [], hasMore: false };
    }

    try {
      const response = await this.firestoreServiceInstance.getAdminWalletTransactions(
        cursor,
        limitCount,
      );

      const transactions = response.items.map(item =>
        AppTransactionRemapper.toEntity(AppTransactionRemapper.fromJson(item)),
      );

      return {
        transactions,
        hasMore: response.hasMore,
        cursor: response.cursor,
      };
    } catch (error) {
      appLogger.error('Error fetching admin transactions', 'WalletService.fetchAdminTransactions', { error: error as Error });
      return { transactions: [], hasMore: false };
    }
  }

  /**
   * Refresh transactions (fetch newer ones since given timestamp)
   */
  async refreshTransactions(
    limitCount: number,
    afterTimestamp: number,
  ): Promise<{ transactions: AppTransactionEntity[]; hasMore: boolean }> {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return { transactions: [], hasMore: false };
    }

    try {
      const response = await this.firestoreServiceInstance.refreshWalletTransactions(
        userId,
        afterTimestamp,
        limitCount
      );

      const transactions = response.items.map(item =>
        AppTransactionRemapper.toEntity(AppTransactionRemapper.fromJson(item)),
      );

      return {
        transactions,
        hasMore: response.hasMore,
      };
    } catch (error) {
      appLogger.error('Error refreshing transactions', 'WalletService.refreshTransactions', { error: error as Error });
      return { transactions: [], hasMore: false };
    }
  }

  /**
   * Listen to live Firestore changes of the current user's wallet document
   * and mirror each change into SecureStorage.
   *
   * Mirrors Flutter's WalletService.listenToWalletChanges
   * (lib/services/wallet_service.dart:13-29): when the wallet document
   * exists with non-empty data, remap it to storage shape and write to
   * SecureStorage under SECURE_STORAGE_KEYS.WALLET. When the document is
   * missing/empty, do nothing — matching the Flutter `if (walletInfo != null)`
   * guard which leaves the local store untouched in that case.
   *
   * Per-snapshot errors are caught + logged via appLogger.error and swallowed
   * so a single bad snapshot cannot break the listener. Stream-level errors
   * surfaced via `onError` are logged with a distinct message. Returns the
   * underlying Firestore Unsubscribe; the caller is responsible for cleanup.
   */
  listenToWalletChanges(userId: string): Unsubscribe {
    const storage = SecureStorageWebCrypto.getInstance();

    return this.firestoreServiceInstance.subscribeToWalletLiveChanges(
      userId,
      async data => {
        if (data === null) {
          return;
        }
        try {
          const dto = WalletRemapper.fromJson(data);
          if (dto === null) {
            return;
          }
          const walletStorage = WalletRemapper.toStorage(dto);
          await storage.setEncrypted(SECURE_STORAGE_KEYS.WALLET, walletStorage);
        } catch (error) {
          appLogger.error(
            'Error syncing wallet change',
            'WalletService.listenToWalletChanges',
            { error: error as Error },
          );
        }
      },
      error => {
        appLogger.error(
          'Error in wallet snapshot listener',
          'WalletService.listenToWalletChanges',
          { error: error as Error },
        );
      },
    );
  }

  /**
   * Get wallet from SecureStorage and map to entity
   */
  async getWalletFromStorage(): Promise<WalletEntity | null> {
    try {
      const storage = SecureStorageWebCrypto.getInstance();
      const walletStorage = await storage.getEncrypted<WalletStorage>(SECURE_STORAGE_KEYS.WALLET);

      if (!walletStorage) {
        return null;
      }

      return WalletRemapper.toEntity(walletStorage);
    } catch (error) {
      appLogger.error('Error getting wallet from storage', 'WalletService.getWalletFromStorage', { error: error as Error });
      return null;
    }
  }
}

// Export singleton instance for convenience
export const walletService = WalletService.getInstance();
